import { useEffect, useMemo, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  type DefaultEntityFilters,
  type EntityFilter,
  catalogReactTranslationRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import { WorkflowTagFilter } from './workflowTagFilter';

// Keep this choice separate from tag values so a real "no-tags" tag stays distinct.
const untaggedQueryFilter: EntityFilter = { toQueryValue: () => 'true' };

export function WorkflowTagPicker({
  filterEntity,
}: {
  filterEntity: (entity: Entity) => boolean;
}) {
  const { backendEntities, loading, updateFilters, queryParameters } =
    useEntityList<DefaultEntityFilters & { untagged?: EntityFilter }>();
  const { t } = useTranslationRef(catalogReactTranslationRef);
  const queryTags = useMemo(
    () =>
      [queryParameters.tags]
        .flat()
        .filter((tag): tag is string => typeof tag === 'string'),
    [queryParameters.tags],
  );
  const [selectedTags, setSelectedTags] = useState(queryTags);
  const queryUntagged = [queryParameters.untagged].flat().includes('true');
  const [includeUntagged, setIncludeUntagged] = useState(queryUntagged);
  const ownerTemplates = useMemo(
    () =>
      backendEntities.filter(
        entity =>
          entity.kind.toLowerCase() === 'template' && filterEntity(entity),
      ),
    [backendEntities, filterEntity],
  );
  const hasUntagged = ownerTemplates.some(
    entity => !entity.metadata.tags?.length,
  );
  const validUntagged = hasUntagged && includeUntagged;
  // Use the owner's source entities so selecting a tag or searching does not
  // remove the owner's other tag choices.
  const availableTags = useMemo(
    () =>
      Array.from(
        new Set(ownerTemplates.flatMap(entity => entity.metadata.tags ?? [])),
      ).sort((a, b) => a.localeCompare(b)),
    [ownerTemplates],
  );
  const validTags = useMemo(
    () => selectedTags.filter(tag => availableTags.includes(tag)),
    [selectedTags, availableTags],
  );

  useEffect(() => {
    setSelectedTags(queryTags);
  }, [queryTags]);
  useEffect(() => {
    setIncludeUntagged(queryUntagged);
  }, [queryUntagged]);

  useEffect(() => {
    if (!loading) {
      updateFilters({
        tags:
          validTags.length || validUntagged
            ? new WorkflowTagFilter(validTags, validUntagged)
            : undefined,
        untagged: validUntagged ? untaggedQueryFilter : undefined,
      });
    }
  }, [loading, validTags, validUntagged, updateFilters]);

  if (!availableTags.length && !hasUntagged) return null;

  return (
    <Box pb={1} pt={1}>
      <Card variant="outlined">
        <Box p={2}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              {t('entityTagPicker.title')}
            </FormLabel>
            <Box component={FormGroup} mt={1}>
              {availableTags.map(tag => (
                <FormControlLabel
                  key={tag}
                  label={tag}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={validTags.includes(tag)}
                      onChange={(_event, checked) =>
                        setSelectedTags(previous =>
                          checked
                            ? [...previous, tag]
                            : previous.filter(value => value !== tag),
                        )
                      }
                    />
                  }
                />
              ))}
              {hasUntagged && (
                <FormControlLabel
                  label="No Tags"
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={validUntagged}
                      onChange={(_event, checked) =>
                        setIncludeUntagged(checked)
                      }
                    />
                  }
                />
              )}
            </Box>
          </FormControl>
        </Box>
      </Card>
    </Box>
  );
}
